import { useState, useEffect, useCallback } from "react";
import { Container, Paper, FormControl, InputLabel, Select, MenuItem, Box, Button, Snackbar, Alert } from "@mui/material";
import RefreshIcon from '@mui/icons-material/Refresh';

import { DailyServingForm } from "../../Components/FoodCost/DailyServingForm";
import { SavedServingsList } from "../../Components/FoodCost/SavedServingsList";

import Product from "../../Services/FoodCost";
import Stock from "../../Services/Stock";
import DailyServings from "../../Services/DailyServing";

import { DailyServingsTable } from "../../Components/FoodCost/DailyServingTable";
import { HeaderDailyServings } from "../../Components/FoodCost/HeaderDailyServings";

const DailyServingsPage = () => {
  const [products, setProducts] = useState([]);
  const [stocks, setStocks] = useState([]);
  const [form, setForm] = useState({ 
    name: "", 
    soldPrice: "", 
    ingredients: [],
    unit: 'piece'
  });
  const [editingProduct, setEditingProduct] = useState(null);
  const [dailyServings, setDailyServings] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [currentServing, setCurrentServing] = useState({ 
    id: null, 
    items: [], 
    total: 0 
  });
  
  // Manual refresh states
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [lastRefreshTime, setLastRefreshTime] = useState(new Date());

  const restaurantId = localStorage.getItem("restaurantId");

  // Data fetching functions
  const fetchProducts = useCallback(async () => {
    try {
      const data = await Product.getAllProducts();
      setProducts(data);
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  }, []);

  const fetchStocks = useCallback(async () => {
    try {
      const data = await Stock.getAllStocks(restaurantId);
      setStocks(data);
    } catch (error) {
      console.error("Error fetching stocks:", error);
    }
  }, [restaurantId]);

  const fetchDailyServings = useCallback(async () => {
    try {
      const data = await DailyServings.getDailyServingsByRestaurantId(restaurantId);
      const normalizedData = data.map(serving => ({
        id: serving.id,
        items: serving.items || [],
        totalCost: parseFloat(serving.totalCost) || 0,
        realCost: parseFloat(serving.realCost) || 0,
        date: serving.date || new Date().toISOString(),
        restaurantId: serving.restaurantId
      }));
      setDailyServings(normalizedData);
      setLastRefreshTime(new Date());
    } catch (error) {
      console.error("Error fetching daily servings:", error);
      setSnackbar({
        open: true,
        message: 'Failed to load data',
        severity: 'error'
      });
    }
  }, [restaurantId]);

  // Manual refresh function
  const handleManualRefresh = async () => {
    setSnackbar({
      open: true,
      message: 'Refreshing data...',
      severity: 'info'
    });
    await Promise.all([fetchProducts(), fetchStocks(), fetchDailyServings()]);
    setSnackbar({
      open: true,
      message: 'Data refreshed successfully',
      severity: 'success'
    });
  };

  // Initial data fetch - only once on component mount
 useEffect(() => {
  // Initial fetch
  fetchProducts();
  fetchStocks();
  fetchDailyServings();

  // Auto refresh every 2 seconds
  const interval = setInterval(() => {
    fetchProducts();
    fetchStocks();
    fetchDailyServings();
  }, 2000);

  // Cleanup when component unmounts
  return () => clearInterval(interval);
}, [fetchProducts, fetchStocks, fetchDailyServings]);

  const handleProductSubmit = async (formData) => {
    try {
      const validIngredients = formData.ingredients.map(ingredient => ({
        ...ingredient,
        unit: Stock.normalizeUnit(ingredient.unit),
        quantity: Number(ingredient.quantity)
      }));

      const productData = {
        ...formData,
        soldPrice: parseFloat(formData.soldPrice),
        ingredients: validIngredients,
        unit: Stock.normalizeUnit(formData.unit)
      };

      const product = new Product(
        productData.name,
        productData.soldPrice,
        restaurantId,
        "",
        productData.ingredients,
        productData.unit
      );

      if (editingProduct) {
        await product.updateProduct(editingProduct, productData);
        setSnackbar({ open: true, message: 'Product updated successfully', severity: 'success' });
      } else {
        await product.addProduct();
        setSnackbar({ open: true, message: 'Product added successfully', severity: 'success' });
      }
      
      await fetchProducts(); // Refresh products after operation
      setForm({ name: "", soldPrice: "", ingredients: [], unit: 'piece' });
      setEditingProduct(null);
    } catch (error) {
      console.error("Error saving product:", error);
      setSnackbar({ open: true, message: 'Error saving product', severity: 'error' });
    }
  };

  // Product handlers
  const handleEdit = (product) => {
    setEditingProduct(product.id);
    setForm({
      ...product,
      unit: product.unit || 'piece',
      ingredients: product.ingredients.map(ing => ({
        ...ing,
        unit: Stock.normalizeUnit(ing.unit)
      }))
    });
  };

  const handleDelete = async (id) => {
    try {
      await new Product().deleteProduct(id);
      await fetchProducts();
      setSnackbar({ open: true, message: 'Product deleted successfully', severity: 'success' });
    } catch (error) {
      console.error("Error deleting product:", error);
      setSnackbar({ open: true, message: 'Error deleting product', severity: 'error' });
    }
  };

  const handleInputChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleUnitChange = (e) => {
    setForm({ ...form, unit: Stock.normalizeUnit(e.target.value) });
  };

  const handleIngredientChange = (index, field, value) => {
    const newIngredients = [...form.ingredients];
    
    if (field === 'stockName') {
      const selectedStock = stocks.find(s => s.productName === value);
      if (selectedStock) {
        newIngredients[index] = {
          ...newIngredients[index],
          [field]: value,
          unit: Stock.normalizeUnit(selectedStock.unit)
        };
      }
    } else {
      newIngredients[index][field] = value;
    }
    
    setForm({ ...form, ingredients: newIngredients });
  };

  const addIngredient = () => {
    setForm({ 
      ...form, 
      ingredients: [...form.ingredients, { 
        stockName: "", 
        quantity: 0,
        unit: 'piece'
      }] 
    });
  };

  const removeIngredient = (index) => {
    const newIngredients = form.ingredients.filter((_, i) => i !== index);
    setForm({ ...form, ingredients: newIngredients });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const validIngredients = form.ingredients.map(ingredient => ({
        ...ingredient,
        unit: Stock.normalizeUnit(ingredient.unit),
        quantity: Number(ingredient.quantity)
      }));

      const productData = {
        ...form,
        soldPrice: parseFloat(form.soldPrice),
        ingredients: validIngredients,
        unit: Stock.normalizeUnit(form.unit)
      };

      const product = new Product(
        productData.name,
        productData.soldPrice,
        restaurantId,
        "",
        productData.ingredients,
        productData.unit
      );

      if (editingProduct) {
        await product.updateProduct(editingProduct, productData);
        setSnackbar({ open: true, message: 'Product updated successfully', severity: 'success' });
      } else {
        await product.addProduct();
        setSnackbar({ open: true, message: 'Product added successfully', severity: 'success' });
      }
      
      await fetchProducts();
      setForm({ name: "", soldPrice: "", ingredients: [], unit: 'piece' });
      setEditingProduct(null);
    } catch (error) {
      console.error("Error saving product:", error);
      setSnackbar({ open: true, message: 'Error saving product', severity: 'error' });
    }
  };

  // Daily serving handlers
  const handleProductSelect = (product) => {
    if (!selectedProducts.some(p => p.id === product.id)) {
      const updatedProducts = [...selectedProducts, { ...product, quantity: 1 }];
      setSelectedProducts(updatedProducts);
      updateTotal(updatedProducts);
    }
  };

  const updateQuantity = (id, quantity) => {
    const updated = selectedProducts.map(p => 
      p.id === id ? { ...p, quantity: Math.max(1, quantity) } : p
    );
    setSelectedProducts(updated);
    updateTotal(updated);
  };

  const updateTotal = (items) => {
    const total = items.reduce((sum, item) => sum + (item.costPrice * item.quantity), 0);
    setCurrentServing(prev => ({ ...prev, items, total }));
  };

  const removeProduct = (id) => {
    const filtered = selectedProducts.filter(p => p.id !== id);
    setSelectedProducts(filtered);
    updateTotal(filtered);
  };

  const addDailyServing = async () => {
    try {
      if (selectedProducts.length === 0) {
        alert("Please select at least one product");
        return;
      }

      const items = selectedProducts.map(p => ({
        productId: p.id,
        productName: p.name,
        quantity: p.quantity,
        costPrice: p.costPrice,
        soldPrice: p.soldPrice
      }));

      const totalCost = items.reduce((sum, item) => sum + (item.costPrice * item.quantity), 0);
      const realCost = items.reduce((sum, item) => sum + (item.soldPrice * item.quantity), 0);
      const date = new Date().toISOString();

      const newServing = new DailyServings(
        items,
        totalCost,
        realCost,
        date,
        restaurantId
      );

      await newServing.addDailyServing();
      await fetchDailyServings(); // Refresh after adding
      resetServingForm();
      setSnackbar({ open: true, message: 'Daily serving added successfully', severity: 'success' });
    } catch (error) {
      console.error("Error saving daily serving:", error);
      setSnackbar({ open: true, message: 'Error saving daily serving', severity: 'error' });
    }
  };

  const editDailyServing = (serving) => {
    setCurrentServing({
      id: serving.id,
      items: serving.items,
      totalCost: serving.totalCost,
      realCost: serving.realCost
    });
    
    setSelectedProducts(serving.items.map(item => ({
      id: item.productId,
      name: item.productName,
      quantity: item.quantity,
      costPrice: item.costPrice,
      soldPrice: item.soldPrice
    })));
  };

  const deleteDailyServing = async (id) => {
    try {
      await DailyServings.deleteDailyServing(id);
      await fetchDailyServings(); // Refresh after deleting
      setSnackbar({ open: true, message: 'Daily serving deleted successfully', severity: 'success' });
    } catch (error) {
      console.error("Error deleting daily serving:", error);
      setSnackbar({ open: true, message: 'Error deleting daily serving', severity: 'error' });
    }
  };

  const resetServingForm = () => {
    setCurrentServing({ id: null, items: [], total: 0 });
    setSelectedProducts([]);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Container maxWidth="lg" sx={{ my: 4 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 4, background: 'linear-gradient(45deg, #f5f5f5 30%, #ffffff 90%)' }}>
        {/* Manual Refresh Button */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mb: 2 }}>
          <Button
            variant="contained"
            onClick={handleManualRefresh}
            startIcon={<RefreshIcon />}
            size="small"
          >
            Refresh Data
          </Button>
        </Box>
        
        {/* Last refresh time indicator */}
        <Box sx={{ textAlign: 'right', mb: 2, color: 'text.secondary', fontSize: '0.875rem' }}>
          Last updated: {lastRefreshTime.toLocaleTimeString()}
        </Box>

        <HeaderDailyServings />
        <DailyServingsTable
          dailyServings={dailyServings}
          editDailyServing={editDailyServing}
          deleteDailyServing={deleteDailyServing}
        />
 
        <DailyServingForm
          products={products}
          selectedProducts={selectedProducts}
          currentServing={currentServing}
          handleProductSelect={handleProductSelect}
          updateQuantity={updateQuantity}
          removeProduct={removeProduct}
          addDailyServing={addDailyServing}
          resetServingForm={resetServingForm}
        />
        <br />

        {dailyServings.length > 0 && (
          <SavedServingsList
            dailyServings={dailyServings}
            editDailyServing={editDailyServing}
            deleteDailyServing={deleteDailyServing}
          />
        )}
      </Paper>
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default DailyServingsPage;